// GARS (Global Area Reference System) — NGA-standard områdereferanse brukt bl.a.
// i luft/ild-koordinering og som felles rutenett når MGRS-presisjon ikke trengs.
// En GARS-celle er 30' (≈ et fast lat/lon-område); kan forfines til 15' (kvadrant)
// og 5' (telefontastatur-nøkkel). Ren, deterministisk konvertering begge veier.
//
// Format:  BBBLLQK
//   BBB = lengdebånd 001–720 (0.5° øst fra 180°V)
//   LL  = breddebånd, to bokstaver (24-alfabet A–Z uten I og O), 0.5° fra 90°S
//   Q   = 15'-kvadrant 1–4 (1=NV, 2=NØ, 3=SV, 4=SØ)   [valgfri]
//   K   = 5'-nøkkel 1–9 (telefontastatur fra NV)        [valgfri]
const ALPHA = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // 24 bokstaver (uten I, O)
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
/** Konverter lat/lon (grader) → GARS-streng. `precision`: 30 | 15 | 5 (minutter). */
export function toGars(lat, lon, precision = 5) {
    // Normaliser lon til [-180,180), lat til [-90,90).
    let lo = ((lon + 180) % 360 + 360) % 360 - 180;
    const la = clamp(lat, -90, 89.9999999);
    if (lo >= 180)
        lo -= 360;
    const band = clamp(Math.floor((lo + 180) / 0.5) + 1, 1, 720);
    const latIdx = clamp(Math.floor((la + 90) / 0.5), 0, 359);
    let s = String(band).padStart(3, '0') + ALPHA[Math.floor(latIdx / 24)] + ALPHA[latIdx % 24];
    if (precision === 30)
        return s;
    const cellMinLon = (band - 1) * 0.5 - 180;
    const cellMinLat = latIdx * 0.5 - 90;
    const lonInCell = lo - cellMinLon; // 0..0.5
    const latInCell = la - cellMinLat; // 0..0.5
    const east = lonInCell >= 0.25 ? 1 : 0; // 0=vest, 1=øst
    const north = latInCell >= 0.25 ? 0 : 1; // 0=nord, 1=sør (radnummer fra nord)
    const quadrant = north === 0 ? (east === 0 ? 1 : 2) : (east === 0 ? 3 : 4);
    s += String(quadrant);
    if (precision === 15)
        return s;
    // 5'-nøkkel: 3×3 innen 15'-kvadranten, telefontastatur (1 NV … 9 SØ).
    const quadMinLon = cellMinLon + east * 0.25;
    const quadMinLat = cellMinLat + (north === 0 ? 0.25 : 0); // nordkvadrant starter høyere
    const cell5 = 0.25 / 3;
    const kcol = clamp(Math.floor((lo - quadMinLon) / cell5), 0, 2);
    const krowFromNorth = clamp(Math.floor((quadMinLat + 0.25 - la) / cell5), 0, 2);
    s += String(krowFromNorth * 3 + kcol + 1);
    return s;
}
/** Parse en GARS-streng → avgrensningsboks (grader). null hvis ugyldig. */
export function garsToBounds(gars) {
    const s = gars.trim().toUpperCase();
    // Nøkkel (5′) kan bare følge en gyldig kvadrant (15′) — ellers er strengen ugyldig.
    if (!/^\d{3}[A-Z]{2}(?:[1-4][1-9]?)?$/.test(s))
        return null;
    const band = Number(s.slice(0, 3));
    if (band < 1 || band > 720)
        return null;
    const i1 = ALPHA.indexOf(s[3]);
    const i2 = ALPHA.indexOf(s[4]);
    if (i1 < 0 || i2 < 0)
        return null;
    const latIdx = i1 * 24 + i2;
    if (latIdx > 359)
        return null;
    let west = (band - 1) * 0.5 - 180;
    let south = latIdx * 0.5 - 90;
    let size = 0.5;
    if (s.length >= 6) {
        const q = Number(s[5]);
        size = 0.25;
        const east = q === 2 || q === 4;
        const north = q === 1 || q === 2;
        if (east)
            west += 0.25;
        if (north)
            south += 0.25;
    }
    if (s.length >= 7) {
        const k = Number(s[6]);
        const cell5 = 0.25 / 3;
        const kcol = (k - 1) % 3; // 0..2 vest→øst
        const krowFromNorth = Math.floor((k - 1) / 3); // 0=nord..2=sør
        west += kcol * cell5;
        south += (2 - krowFromNorth) * cell5; // rad 0 (nord) = øverste → høyest sør-grense
        size = cell5;
    }
    return { south, west, north: south + size, east: west + size };
}
/** Senterpunkt (lat/lon) for en GARS-celle. null hvis ugyldig. */
export function garsCenter(gars) {
    const b = garsToBounds(gars);
    if (!b)
        return null;
    return { lat: (b.south + b.north) / 2, lon: (b.west + b.east) / 2 };
}
//# sourceMappingURL=gars.js.map